<script module>
  export const i18n = {
    sv: {
      'Timetables for this date are not available yet. Try again later or with a date not as far in the future.':
        'Tidtabeller för detta datum är ännu inte tillgängliga. Försök igen senare eller med ett datum som inte ligger så långt fram i tiden.',
      'reloading the page': 'ladda om sidan',
      'Try %s or try with another date or route.':
        'Prova att %s eller försök med ett annat datum eller en annan rutt.',
      'Show more trains': 'Visa fler tåg',
      'Show earlier trains': 'Visa tidigare tåg'
    }
  }
</script>

<script>
  import { slide } from 'svelte/transition'
  import { quintOut } from 'svelte/easing'
  import { flip } from 'svelte/animate'

  import Journey, { JourneyWithAvailabilityFragment } from '$lib/Journey.svelte'
  import { register } from '$lib/utils/text.svelte.js'
  import State from '$lib/utils/state.svelte.js'
  import { gql } from '$lib/graphql.svelte.js'
  import Splash from '$lib/Splash.svelte'

  /** @typedef {Schema.SmartJourney|Schema.BlueprintJourney} JourneyTypes */

  const PLACEHOLDER = { id: 'PLACEHOLDER', __typename: 'PlaceholderJourney' }

  let text = register(i18n)

  /** @type {{
   * origin?: Schema.Location?,
   * destination?: Schema.Location?,
   * via?: Schema.Location?,
   * duration?: string?,
   * departureDate?: string?,
   * passengers: Schema.PassengerPlaceholderInput[],
   * blueprint?: string?,
   * isPassholder?: boolean,
   * disabled?: boolean,
   * splash?: boolean,
   * action?: Svelte.Snippet<[Schema.Journey, App.GraphQLOperationResult<{ journey: Schema.JourneyOffer }>, { selected: boolean }]>,
   * onselect?: function(Schema.JourneyOffer): void,
   * onerror?: function(Error): void,
   * error?: Svelte.Snippet<[string]>,
   * }}*/
  const {
    origin = null,
    destination = null,
    via = null,
    duration = null,
    departureDate,
    passengers,
    blueprint = null,
    isPassholder = false,
    disabled = false,
    splash = false,
    onselect,
    onerror,
    action,
    error
  } = $props()

  /** @param {Schema.Journey?} [journey] */
  const isPresentable = (journey) =>
    Boolean(
      journey &&
        journey.status !== 'ERROR' &&
        journey.itinerary?.every(
          (item) =>
            item.__typename !== 'SegmentCollection' || item.segments?.length
        )
    )

  /** @param {Schema.Journey} journey */
  const getDepartureDate = (journey) =>
    new Date(
      journey.itinerary
        ?.filter((item) => item.__typename === 'SegmentCollection')
        .flatMap(
          (/** @type {Schema.SegmentCollection} */ item) => item.segments
        )
        .at(0)?.departureAt
    )

  /** @type {App.GraphQLOperation<{ journeys: Schema.SmartJourney[] }, { origin: string, destination: string, departureDate: string, via?: Schema.ViaInput[], passengers: Schema.PassengerPlaceholderInput[], currency?: string }>} */
  const GetJourneys = gql`
    subscription GetJourneys(
      $origin: ID!
      $destination: ID!
      $departureDate: Date!
      $via: [ViaInput!] = []
      $passengers: [PassengerPlaceholderInput!]
      $currency: String
    ) {
      journeys: getJourneys(
        origin: $origin
        destination: $destination
        date: $departureDate
        via: $via
        passengers: $passengers
        filter: { types: [SMART] }
        currency: $currency
      ) {
        ...${JourneyWithAvailabilityFragment}
      }
    }
  `

  /** @type {App.GraphQLOperation<{ journey: Schema.BlueprintJourney }, { id: string, date: string, passengers: Schema.PassengerPlaceholderInput[], currency?: string }>} */
  const GetBlueprint = gql`
    query GetBlueprint($id: ID!, $date: Date!, $passengers: [PassengerPlaceholderInput!], $currency: String) {
      journey: getJourneyForBlueprint(blueprint: $id, date: $date, passengers: $passengers, currency: $currency) {
        ...${JourneyWithAvailabilityFragment}
      }
    }
  `

  let selectedJourneyId = $state()

  let expanded = $state(false)

  let stackNonBlueprintJourneys = $state(Boolean(blueprint))

  let blueprintExclusive = $derived(
    stackNonBlueprintJourneys && isPresentable(GetBlueprint.data?.journey)
  )

  $effect(() => {
    if (blueprint && departureDate) {
      return GetBlueprint.execute({
        id: blueprint,
        passengers,
        date: departureDate,
        currency: State.currency
      })
    }
  })

  $effect(() => {
    if (origin && destination && departureDate) {
      expanded = false
      return GetJourneys.execute({
        origin: origin.uid,
        destination: destination.uid,
        via: via ? [{ uid: via.uid, duration }] : [],
        departureDate,
        passengers,
        currency: State.currency
      })
    }
  })

  $effect(() => {
    if (GetJourneys.error && onerror) {
      onerror(GetJourneys.error)
    }
  })

  let journeys = $derived(
    /** @type {JourneyTypes[]} */ (
      GetJourneys.stale || !GetJourneys.data?.journeys
        ? []
        : GetJourneys.data.journeys.filter(isPresentable)
    )
      .concat(
        GetBlueprint?.data?.journey && isPresentable(GetBlueprint.data?.journey)
          ? GetBlueprint?.data?.journey
          : []
      )
      .sort((a, b) => {
        if (blueprintExclusive) {
          return a.__typename === 'BlueprintJourney' ? 1 : -1
        }
        return +getDepartureDate(a) - +getDepartureDate(b)
      })
  )

  let stacked = $derived(
    // Dummy type to allow calling indexOf with any type
    /** @type {any[]}  */ (
      journeys.length > 8
        ? journeys
            .filter((journey) => {
              const departureDate = getDepartureDate(journey)
              return (
                departureDate.getHours() < 8 ||
                (blueprintExclusive &&
                  journey.__typename !== 'BlueprintJourney')
              )
            })
            .reverse()
        : []
    )
  )

  let unstacked = $derived.by(() => {
    /** @type {(JourneyTypes|typeof PLACEHOLDER)[]} */
    const items = journeys.filter(
      (journey) => !showStack || stacked.indexOf(journey) < 2
    )

    if (GetJourneys.fetching) {
      items.push(PLACEHOLDER)
    }

    return items
  })

  let showStack = $derived(
    Boolean(
      stacked.length &&
        !expanded &&
        (blueprintExclusive || stacked.length + 3 < journeys.length)
    )
  )

  let hasError = $derived(
    !journeys.length &&
      !GetJourneys.fetching &&
      !GetJourneys.stale &&
      (GetJourneys.error ||
        GetJourneys.data?.journeys.every(
          (journey) => journey.status === 'ERROR'
        ))
  )

  // Work around limitations in type casting inside templates
  const asJourney = (item) => /** @type {JourneyTypes} */ (item)

  const getErrorMessage = () => {
    const statuses = journeys?.map((journey) =>
      journey.itinerary
        ?.filter((item) => item.__typename === 'SegmentCollection')
        .flatMap((/** @type {Schema.SegmentCollection} */ item) => item.status)
    )

    if (
      statuses?.every((statuses) => statuses?.includes('OUTSIDE_TIMETABLE'))
    ) {
      return text`Timetables for this date are not available yet. Try again later or with a date not as far in the future.`
    }

    return text`Try ${`<a href="#" class="RichText-a" data-sveltekit-reload>
      ${text`reloading the page`}
    </a>`} or try with another date or route.`
  }
</script>

{#snippet content()}
  <ol class="items">
    {#if showStack}
      <li class="item expand">
        <button
          {disabled}
          class="expander"
          style:--stacked={Math.min(stacked.length, 3)}
          onclick={(event) => {
            expanded = true
            stackNonBlueprintJourneys = false
            event.preventDefault()
          }}>
          <span class="handle">
            {blueprintExclusive
              ? text`Show more trains`
              : text`Show earlier trains`}
          </span>
        </button>
      </li>
    {/if}
    {#if !GetJourneys.stale && journeys.length}
      {#each unstacked as item (item.id)}
        {@const isStacked = showStack && stacked.indexOf(item) > -1}
        <li
          class="item theme-reset"
          class:stacked={isStacked}
          in:slide={{ duration: 600, easing: quintOut }}
          animate:flip={{ duration: 600, easing: quintOut }}>
          {#if item === PLACEHOLDER}
            <Journey {origin} {destination} />
          {:else}
            {@const journey = asJourney(item)}
            <Journey
              {origin}
              {destination}
              {passengers}
              {isPassholder}
              {disabled}
              {action}
              {journey}
              onselect={(journeyOffer) => {
                selectedJourneyId = journey.id
                onselect?.(journeyOffer)
              }}
              expanded={isStacked
                ? false
                : journey.__typename === 'BlueprintJourney'}
              selected={selectedJourneyId === journey.id} />
          {/if}
        </li>
      {/each}
    {:else if !hasError}
      <li
        class="item theme-reset"
        in:slide={{ duration: 600, easing: quintOut }}>
        <Journey {origin} {destination} />
      </li>
    {/if}
  </ol>
  {#if hasError}
    {@render error?.(getErrorMessage())}
  {/if}
{/snippet}

<div class="journeys">
  {#if origin && destination}
    {#if splash}
      <Splash
        end={Boolean(!GetJourneys.stale && (journeys.length || hasError))}>
        {@render content()}
      </Splash>
    {:else}
      {@render content()}
    {/if}
  {/if}
</div>

<style>
  .journeys {
    display: grid;
    gap: 1.125rem;
  }

  .items {
    display: grid;
    gap: 1.25rem;
  }

  .item {
    position: relative;
    transition: margin-bottom 600ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .item.expand {
    display: flex;
    align-items: center;
    z-index: 1;
    height: 0;
    width: 100%;
    justify-content: center;
  }

  .expander {
    font-weight: 500;
    width: 100%;
    display: block;
    padding: 1rem 0;
    margin-top: calc(1.2rem * var(--stacked));
  }

  .handle {
    background: rgb(var(--rgb-bright-gray));
    display: inline-block;
    padding: 0.4rem 0.6rem;
    user-select: none;
    font-size: 0.875rem;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    border-radius: var(--border-radius-full);
    transition: var(--box-shadow-transition);
  }

  .handle:hover {
    cursor: pointer;
    box-shadow: var(--box-shadow-sm);
  }

  .item.stacked {
    --rgb-text: var(--rgb-light-gray);

    margin-bottom: -100%; /* Pull up next item */
    margin-top: -1.25rem;
    pointer-events: none;
    transform: translateY(0.5rem) scale(0.96);
    transform-origin: top center;
  }

  .item.stacked + .item::after {
    content: '';
    width: 100%;
    height: calc(var(--border-radius-lg) * 2);
    position: absolute;
    top: 0;
    pointer-events: none;
    border-radius: var(--border-radius-lg);
    box-shadow: var(--box-shadow-sm);
    transform: rotate(180deg);
    opacity: 0.5;
  }

  .item.stacked:global(:has(+ .item.stacked)) {
    transform: scale(0.88);
    /* opacity: 0.5; */
  }

  .item.stacked:global(:has(+ .item.stacked))::after {
    top: calc(1.65rem + 1.25rem);
  }
</style>
